/**
 * Приложения: то же хозяйство, собранное по смыслу, а не по машинам.
 *
 * Матрица отвечает на вопрос «что где стоит и чем защищено». Этот экран — на
 * другой: **сколько у нас на самом деле магазинов и защищён ли магазин**.
 * Три копии одного магазина в матрице выглядят тремя разными сайтами, и
 * «восстановить магазин» превращается в выбор из похожих имён — там, где цена
 * ошибки наибольшая: восстановление не туда затирает работающее.
 *
 * Что попадает сюда: **есть интерфейс и есть данные** — своя база или то, что
 * приложение порождает. Обратный прокси под определение не подходит:
 * интерфейс есть, а терять нечего, всё восстанавливается пересборкой.
 */
import { useState } from 'react';
import { useQuery } from '../transport/hooks.ts';
import { request } from '../transport/socket.ts';
import { StatusBadge } from '../components/StatusBadge.tsx';
import { Hint } from '../components/Hint.tsx';
import { EditName } from '../components/EditName.tsx';
import { LEVEL_LABELS } from '../../shared/domain/status.ts';
import {
  DEPLOYMENT_ROLES, ROLE_LABELS, SERVICE_KINDS, SERVICE_KIND_LABELS,
} from '../../shared/domain/services.ts';
import type { OverviewSnapshot, ServiceDeployment, ServiceView } from '../../shared/protocol/overview.ts';

const PLACE_GLYPH: Record<string, string> = {
  machine: '▢', hypervisor: '▤', cloud: '☁', location: '⌂',
};

function Deployment({ d, onRole, onRename, busy }: {
  d: ServiceDeployment;
  onRole: (sourceKey: string, role: string) => void;
  onRename: (key: string, label: string, note: string) => Promise<void>;
  busy: boolean;
}): React.JSX.Element {
  const dim = d.lifecycle === 'retired' || d.role === 'archive';

  return (
    <li className={`dep dep--${d.role}${dim ? ' dep--dim' : ''}`}>
      {/*
        Роль меняется прямо здесь, а не правкой файла и пересевом.
        Идентификатор копии при этом остаётся прежним: это префикс в
        хранилище, переименование бросает историю. Роль — указатель, и
        переставить его ничего не стоит; ровно поэтому она и меняется списком,
        а не переносом данных.
      */}
      <select
        className={`dep__role dep__role--${d.role}`}
        value={d.role}
        disabled={busy}
        onChange={(e) => onRole(d.sourceKey, e.target.value)}
        title="Роль копии — решение владельца, а не свойство машины"
        aria-label={`Роль копии «${d.name}»`}
      >
        {DEPLOYMENT_ROLES.map((r) => (
          <option key={r} value={r}>{ROLE_LABELS[r]}</option>
        ))}
      </select>

      <span className="dep__name">
        <EditName
          objectKey={d.sourceKey}
          shown={d.name}
          {...(d.factName ? { factName: d.factName } : {})}
          {...(d.note ? { note: d.note } : {})}
          busy={busy}
          onSave={onRename}
        />
        {d.lifecycle === 'planned' && <span className="dep__tag">планируется</span>}
        {d.lifecycle === 'retired' && <span className="dep__tag">снята с работы</span>}
      </span>

      <span className="dep__place">
        {d.place.map((p) => (
          <span key={p.label} title={p.title}>
            <span aria-hidden="true">{PLACE_GLYPH[p.kind]}</span> {p.label}
          </span>
        ))}
      </span>

      <span className="dep__verdict">
        <StatusBadge status={d.verdict} />
      </span>

      {/*
        Дыры перечисляются поимённо, а не сводятся в «не в норме». «Нет копии
        базы» и «нет копии медиатеки» — разные новости: первую можно снять
        дампом за минуты, вторую нельзя ничем, кроме самой копии.
      */}
      <span className="dep__gaps">
        {d.gaps.length > 0
          ? d.gaps.map((g) => (
              <span key={g} className="dep__gap">нет копии: {LEVEL_LABELS[g].toLowerCase()}</span>
            ))
          : <span className="muted">—</span>}
      </span>

      {d.agentSiteId && (
        <span className="dep__agent" title="Этой копией занимается агент резервного копирования">
          агент · <span className="mono">{d.agentSiteId.split('/')[1]}</span>
        </span>
      )}
    </li>
  );
}

function Service({ s, onRole, onRename, busy }: {
  s: ServiceView;
  onRole: (sourceKey: string, role: string) => void;
  onRename: (key: string, label: string, note: string) => Promise<void>;
  busy: boolean;
}): React.JSX.Element {
  const copies = s.deployments.length;
  const working = s.deployments.filter(
    (d) => d.role !== 'archive' && d.lifecycle !== 'retired' && d.lifecycle !== 'planned',
  ).length;

  return (
    <article className={`svc svc--${s.verdict}${s.criticality === 'critical' ? ' svc--critical' : ''}`}>
      <header className="svc__head">
        <div className="svc__id">
          <h3 className="svc__name">
            <EditName
              objectKey={s.key}
              shown={s.name}
              {...(s.factName ? { factName: s.factName } : {})}
              {...(s.note ? { note: s.note } : {})}
              busy={busy}
              onSave={onRename}
            />
          </h3>
          <span className="svc__kind">{SERVICE_KIND_LABELS[s.kind]}</span>
          {s.criticality === 'critical' && <span className="svc__crit">критично</span>}
        </div>

        <div className="svc__state">
          {/*
            Вердикт основной копии отдельно от общего. Пропущенный проход у
            основной — происшествие; у проверочной — шум. Одинаковая тревога
            на оба случая через месяц перестаёт читаться, и пропущенной
            окажется настоящая.
          */}
          {s.primaryVerdict && (
            <Hint content={
              <div className="hint__body">
                <b className="hint__title">Основная копия</b>
                <p className="hint__text">
                  По ней и стоит судить, защищено ли приложение. Остальные копии
                  полезны, но восстанавливать будут эту.
                </p>
              </div>
            }>
              <span className="svc__primary">
                основная <StatusBadge status={s.primaryVerdict} />
              </span>
            </Hint>
          )}
          <span className="svc__count" title={`копий всего ${copies}, из них работающих ${working}`}>
            {copies === 1 ? 'одна копия' : `копий: ${copies}`}
            {copies !== working && <span className="muted"> · работают {working}</span>}
          </span>
        </div>
      </header>

      {s.purpose && <p className="svc__purpose">{s.purpose}</p>}

      <ul className="deps">
        {s.deployments.map((d) => (
          <Deployment key={d.sourceKey} d={d} onRole={onRole} onRename={onRename} busy={busy} />
        ))}
        {s.deployments.length === 0 && (
          <li className="muted">Ни одного развёртывания — приложение объявлено, но нигде не стоит.</li>
        )}
      </ul>
    </article>
  );
}

export function Services(): React.JSX.Element {
  const { data, error, loading, reload } = useQuery<OverviewSnapshot>('overview.get');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const setRole = async (sourceKey: string, role: string): Promise<void> => {
    setBusy(true);
    setFailure(null);
    try {
      await request('inventory.bind', { key: sourceKey, role });
      reload();
    } catch (err) {
      setFailure((err as { msg?: string }).msg ?? 'не получилось сменить роль');
    } finally {
      setBusy(false);
    }
  };

  /**
   * Одна правка на весь экран — и для приложения, и для копии.
   *
   * Метод один и тот же: и приложение, и копия — объекты инвентаря, и решения
   * владельца про них хранятся одинаково. Две разные функции здесь означали бы
   * два места, где однажды разойдётся поведение.
   */
  const rename = async (key: string, label: string, note: string): Promise<void> => {
    setBusy(true);
    setFailure(null);
    try {
      await request('inventory.bind', { key, label, note });
      reload();
    } catch (err) {
      setFailure((err as { msg?: string }).msg ?? 'не получилось переименовать');
    } finally {
      setBusy(false);
    }
  };

  const createService = async (): Promise<void> => {
    const name = prompt('Как называется приложение? Например: «Интернет-магазин».');
    if (!name) return;
    const key = prompt(
      'Короткий идентификатор: строчные, цифры и дефис.\n' +
      'Например: shop. Менять его потом нельзя.',
    );
    if (!key) return;
    const kind = prompt(
      `Вид приложения — одно из:\n${SERVICE_KINDS.map((k) => `  ${k} — ${SERVICE_KIND_LABELS[k]}`).join('\n')}`,
      'web',
    );
    if (!kind || !SERVICE_KINDS.includes(kind as never)) return;

    setBusy(true);
    setFailure(null);
    try {
      await request('service.create', { key, name, kind });
      reload();
    } catch (err) {
      setFailure((err as { msg?: string }).msg ?? 'не получилось завести приложение');
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    return (
      <section className="panel panel--fill">
        <h2 className="panel__title">Приложения</h2>
        <p className="alert alert--error">Ошибка: {error.msg}</p>
      </section>
    );
  }
  if (!data) {
    return (
      <section className="panel panel--fill">
        <h2 className="panel__title">Приложения</h2>
        <p className="muted">{loading ? 'Собираем…' : 'Нет данных.'}</p>
      </section>
    );
  }

  const multi = data.services.filter((s) => s.deployments.length > 1).length;

  return (
    <section className="panel panel--fill">
      <header className="panel__head">
        <div className="agents__bar">
          <h2 className="panel__title">Приложения</h2>
          <button type="button" className="button" onClick={createService} disabled={busy}>
            Завести приложение
          </button>
        </div>
        {failure && <p className="alert alert--error">{failure}</p>}
        <p className="svc__lead">
          {data.services.length} приложений{multi > 0 && `, у ${multi} несколько копий`}.
          Приложение здесь — то, у чего есть интерфейс и свои данные. Прокси и шлюзы
          в список не входят: терять у них нечего, всё восстанавливается пересборкой.
        </p>
      </header>

      <div className="panel__scroll">
        <div className="svcs">
          {data.services.map((s) => (
            <Service key={s.key} s={s} onRole={setRole} onRename={rename} busy={busy} />
          ))}
        </div>
      </div>

      <footer className="panel__foot">
        <span className="legend__item">роль — решение владельца, а не свойство машины</span>
        <span className="legend__item">идентификатор копии не меняется, роль — свободно</span>
      </footer>
    </section>
  );
}
